import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart line subscription switcher.
 *
 * Lets the shopper change — or drop — the subscription selling plan of a cart
 * line directly in the cart drawer / cart page. Seal Subscriptions uses NATIVE
 * Shopify selling plans, so we switch through the Cart API (POST /cart/change.js
 * with a `selling_plan`) and then fire the theme's CartLinesUpdateEvent so every
 * `cart-items-component` morphs its section — the exact same refresh flow the
 * theme uses for quantity edits (see assets/component-cart-items.js).
 *
 * Markup contract (rendered in snippets/cart-products.liquid):
 *   <select data-cart-plan-select
 *           data-line-key="<line item key>"
 *           data-line="<1-based line number>"
 *           data-quantity="<current qty>">
 *     <option value="">One-time</option>
 *     <option value="<selling_plan id>">Every month</option> …
 *   </select>
 */
(function () {
  const BOUND_FLAG = '__cartPlanSwitchBound';

  /** Theme is a classic-script global (snippets/scripts.liquid) — read defensively. */
  function routes() {
    return (typeof Theme !== 'undefined' && Theme.routes) || {};
  }

  /** All cart section ids on the page (drawer + cart page can coexist). */
  function cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
  }

  function setLoading(select, loading) {
    const wrapper = select.closest('.cart-items__plan');
    if (wrapper) wrapper.classList.toggle('is-loading', loading);
    select.disabled = loading;
    // Mirror the theme's own "busy" styling so totals/rows read as updating.
    document.querySelectorAll('cart-items-component').forEach((c) => {
      c.classList.toggle('cart-items-disabled', loading);
    });
  }

  async function switchPlan(select) {
    if (select.dataset.busy === 'true') return;
    select.dataset.busy = 'true';

    const lineKey = select.getAttribute('data-line-key');
    const quantity = parseInt(select.getAttribute('data-quantity'), 10) || 1;
    const value = select.value;

    const changeUrl = routes().cart_change_url || '/cart/change.js';
    const sections = cartSectionIds();

    const body = {
      id: lineKey,
      quantity,
      // A number switches the plan; `null` returns the line to a one-time purchase.
      selling_plan: value ? Number(value) : null,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    };

    setLoading(select, true);

    // Notify the theme up-front (like product-form / bundle-builder) so any cart
    // UI that reacts to the event stays in sync. Guarded so an event-API change
    // never blocks the actual cart mutation.
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [{ id: lineKey, quantity }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[cart-plan] CartLinesUpdateEvent unavailable', error);
    }

    try {
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      // Cart API sets `status` only on error.
      if (data.status && Number(data.status) >= 400) {
        console.error('[cart-plan] /cart/change.js error', data);
        deferred?.resolve({ cart: null, detail: { didError: true } });
        // Reset the <select> to what it was before the failed change.
        resetSelect(select);
        return;
      }

      // Resolving with the section HTML lets each cart-items-component morph its
      // own section (updated price, plan label, and this <select>'s selected state).
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          items: data.items,
          source: 'cart-subscription-select',
          didError: false,
        },
      });
    } catch (error) {
      console.error('[cart-plan] change failed', error);
      deferred?.resolve({ cart: null, detail: { didError: true } });
      resetSelect(select);
    } finally {
      // The successful path re-renders the row from the server, replacing this
      // element; the guards below only matter when the DOM was left in place.
      if (select.isConnected) {
        select.dataset.busy = 'false';
        setLoading(select, false);
      } else {
        document.querySelectorAll('cart-items-component').forEach((c) => {
          c.classList.remove('cart-items-disabled');
        });
      }
    }
  }

  /** Restore the previously-selected option after a failed change. */
  function resetSelect(select) {
    const original = select.querySelector('option[selected]');
    if (original) select.value = original.value;
  }

  function onChange(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const select = target.closest('[data-cart-plan-select]');
    if (!select) return;
    switchPlan(select);
  }

  // Delegated once on document, so it also covers selects re-rendered by morph.
  if (!document[BOUND_FLAG]) {
    document[BOUND_FLAG] = true;
    document.addEventListener('change', onChange);
  }
})();
